/* eslint-disable @typescript-eslint/explicit-module-boundary-types */
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Box } from 'native-base';
import {
    resizedUrl,
    Stashboard,
    StashboardMedia,
    useAppSettings,
    useSendEvent,
    useSpringBigStore,
} from '@springbig/shared';
import _ReactPlayer, { ReactPlayerProps } from 'react-player';
import { useAppNavigate } from '~/pages';
import VisibilitySensor from 'react-visibility-sensor';
import ClaimGiftCardModal from '~/shared/ClaimGiftCardModal';
import { Caption } from '~/shared/Typography';
import { Button } from '~/shared/Theme';
import { DarkSkeleton } from '~/shared/DarkSkeleton';

const ReactPlayer = _ReactPlayer as unknown as React.FC<ReactPlayerProps>;

export const CarouselCard = ({
    item,
    index,
    stashboard,
    setImgHeight,
    currentIndex,
}: {
    item: StashboardMedia,
    index: number,
    stashboard: Stashboard,
    setImgHeight: (height: number) => void,
    currentIndex: number,
}) => {

    const { fontFamily } = useAppSettings();
    const { member, merchant } = useSpringBigStore();
    const { params, navigate } = useAppNavigate();
    const { mutate: sendEvent } = useSendEvent();
    const [isVisible, setIsVisible] = useState(false);
    const imgRef = useRef(null);
    const [imgWidth, setImgWidth] = useState(0);
    const [currentGiftCardRewardGrant, setCurrentGiftCardRewardGrant] = useState(null);

    const updateImageDimensions = () => {

        if (imgRef.current) {
            const height = imgRef.current.clientHeight;
            const width = imgRef.current.clientWidth;
            setImgHeight(height);
            setImgWidth(width);
        }
    };

    useEffect(() => {

        if (!isVisible) {
            return;
        }

        const handleResize = () => updateImageDimensions();
        window.addEventListener('resize', handleResize);
        return () => window.removeEventListener('resize', handleResize);
    }, [isVisible]);

    const handleImageLoad = () => {

        updateImageDimensions();
    };

    const isFullScreenImage = useMemo(() => stashboard?.full_image_content, [stashboard?.full_image_content]);
    const mediaType = useMemo(() => item?.media_type, [item?.media_type]);

    if (isFullScreenImage) {
        return (
            <>
                <Box key={index} position="relative" w="100%">
                    { isVisible &&
                        <img
                            ref={imgRef}
                            src={resizedUrl(item.result_url, 9999, 480, 'inside')}
                            alt=""
                            width="100%"
                            style={{
                                opacity: isVisible && (imgWidth !== 0 || imgRef?.current?.clientWidth !== 0) ? 1 : 0,
                            }}
                            onLoad={handleImageLoad}
                            loading="lazy"
                        />
                    }
                    <VisibilitySensor onChange={(visibility) => visibility && setIsVisible(visibility)}>
                        <Box height="350px">
                            <DarkSkeleton height="350px" width="100%" opacity={imgRef?.current?.clientWidth ? 0 : 1} />
                        </Box>
                    </VisibilitySensor>

                    { stashboard.full_image_content.map((full_image_content, index0) =>

                        full_image_content.buttons.map((button, index1) => {

                            if (index0 !== index) {
                                return <Box key={`empty-${index0}-${index1}`} />;
                            }

                            const isGiftCard = button.ext_type === 'gift_card';
                            const rewardGrant = member?.reward_grants?.find(
                                (rg) => rg.reward.id === button.ext_reward_id
                            );

                            const handlePress = () => {
                                if (member) {
                                    sendEvent({
                                        event: 'click',
                                        sbk: stashboard._key,
                                        event_owner_id: button.id,
                                        event_owner_type: 'StashboardImageButton',
                                        source: 'wallet',
                                    });
                                }

                                if (isGiftCard) {
                                    if (rewardGrant) {
                                        setCurrentGiftCardRewardGrant(rewardGrant.id);
                                    } else {
                                        navigate(`w/${params.wallet_id}/giftCard`);
                                    }
                                } else {
                                    if (button.ext_type === 'gift_card') {
                                        const target = button.target_url.split('/').splice(4);
                                        const page = target[1].split('?')[0];
                                        const param = target[1]?.match(/reward=(\d+)/);
                                        navigate(`w/${target[0]}/${page}/${param ? param[1] : ''}`);
                                    } else {
                                        window.open(button.target_url);
                                    }
                                }

                                if (merchant?.enhanced_reporting_enabled) {
                                    sendEvent({
                                        event: 'click',
                                        sbk: stashboard._key,
                                        event_owner_id: button.id,
                                        event_owner_type: 'StashboardImageButton',
                                        source: 'wallet',
                                        process_type: stashboard.owner_type,
                                        process_id: stashboard.owner_id,
                                    });
                                }
                            };

                            return (
                                <Button
                                    key={`${index0}, ${index1}`}
                                    borderColor={button.style === 'visible' ? button.border_color : 'transparent'}
                                    borderWidth={1}
                                    position="absolute"
                                    w={`${button.width}%`}
                                    h={`${imgWidth * button.height / 100}px`}
                                    top={`${imgWidth * button.y / 100}px`}
                                    left={`${button.x}%`}
                                    backgroundColor={button.style === 'visible' ? button.background_color : 'transparent'}
                                    p={0}
                                    m={0}
                                    overflow="hidden"
                                    display="flex"
                                    alignItems="center"
                                    justifyContent="center"
                                    borderRadius={button.border_radius || 0}
                                    onPress={handlePress}
                                    modifier="unlocked"
                                >
                                    <Caption
                                        textAlign="center"
                                        fontSize={`${(button.font_size * imgWidth) / 100}px`}
                                        lineHeight={`${(button.font_size * imgWidth) / 100}px`}
                                        color={button.text_color}
                                        fontFamily={button.text_font ?? `${button.text_font}, ${fontFamily}`}
                                    >
                                        {button.text}
                                    </Caption>
                                </Button>
                            );
                        })
                    )}
                </Box>
                {currentGiftCardRewardGrant && (
                    <ClaimGiftCardModal
                        rewardGrantId={currentGiftCardRewardGrant}
                        closeModal={() => setCurrentGiftCardRewardGrant(null)}
                    />
                )}
            </>
        );
    }

    if (mediaType === 'video') {
        return (
            <VisibilitySensor onChange={(visibility) => setIsVisible(visibility && currentIndex === index)}>
                <Box key={index} w="100%" height="96" zIndex={99999}>
                    <ReactPlayer url={item.result_url} width="100%" height="100%" playing={isVisible} loop muted controls />
                </Box>
            </VisibilitySensor>
        );
    }

    if (mediaType === 'image') {
        return (
            <Box key={index} position="relative" w="100%">
                { isVisible &&
                    <img
                        ref={imgRef}
                        src={resizedUrl(item.result_url, 9999, 480, 'inside')}
                        alt=""
                        width="100%"
                        style={{
                            opacity: isVisible && (imgWidth !== 0 || imgRef?.current?.clientWidth !== 0) ? 1 : 0,
                            minHeight: '350px',
                        }}
                        onLoad={handleImageLoad}
                        loading="lazy"
                    />
                }
                <VisibilitySensor onChange={(visibility) => visibility && setIsVisible(visibility)}>
                    <Box height="350px">
                        <DarkSkeleton height="350px" width="100%" opacity={imgWidth !== 0 || imgRef?.current?.clientWidth !== 0 ? 0 : 1} />
                    </Box>
                </VisibilitySensor>
            </Box>
        );
    }
};
